import React from 'react';
import { Home, ChevronRight } from 'lucide-react';

export interface NavigationNode {
  nodeId: string;
  label: string;
}

interface BreadcrumbsProps {
  path: NavigationNode[];
  onNavigate: (index: number) => void; // -1 for root, otherwise index in path
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ path, onNavigate }) => {
  return (
    <div className="bg-gradient-to-r from-white to-gray-50 border-2 border-purple-200 rounded-lg shadow-lg flex items-center gap-1 px-4 py-2.5 max-w-[700px] overflow-x-auto backdrop-blur-sm">
      {/* Root Level */}
      <button
        onClick={() => onNavigate(-1)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-purple-100 hover:border-purple-300 transition-all duration-150 text-sm font-semibold text-gray-700 hover:text-purple-700 whitespace-nowrap border border-transparent hover:border-purple-300 hover:scale-105 active:scale-95"
        title="Navigate to root level (Press Backspace)"
      >
        <Home size={16} />
        <span>Root</span>
      </button>

      {/* Nested Levels */}
      {path.map((node, index) => (
        <React.Fragment key={node.nodeId}>
          <ChevronRight size={16} className="text-purple-400 flex-shrink-0 mx-1" />
          <button
            onClick={() => onNavigate(index)}
            className={`px-3 py-1.5 rounded-md transition-all duration-150 text-sm font-medium whitespace-nowrap border ${
              index === path.length - 1
                ? 'text-purple-700 bg-purple-100 border-purple-300 font-bold cursor-default shadow-sm'
                : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700 hover:border-purple-200 border-transparent hover:scale-105 active:scale-95'
            }`}
            title={index === path.length - 1 ? `Current level: ${node.label}` : `Navigate to ${node.label}`}
            disabled={index === path.length - 1}
          >
            {node.label}
          </button>
        </React.Fragment>
      ))}
      
      {/* Helper text */}
      {path.length > 0 && (
        <div className="ml-3 pl-3 border-l border-purple-200 text-xs text-gray-500 hidden sm:block">
          Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded border border-gray-300 font-mono">Backspace</kbd> to go back
        </div>
      )}
    </div>
  );
};

export default Breadcrumbs;
